/**
 * useSheetData.js
 *
 * Loads all data from the backend API (Postgres) — no Sheets reads.
 * Fires 6 parallel requests so fast tables render immediately while slower
 * ones (outreach, calendar) finish in the background. Caches for 5 minutes.
 * Exposes refresh() and refreshOutreach().
 *
 * Returned shape is identical to before so all consuming pages are unchanged:
 *   { contacts, firms, demos, trials, customers, outreachLog, config, gmailUnmatched }
 *
 * Stale-while-revalidate: on page load, instantly renders the last cached
 * result from localStorage while fetching fresh data in the background.
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import {
  fetchTodayBundle,
  fetchContactEmailMap,
  fetchCalendarUnknownAttendees,
  fetchGmailUnmatched,
  fetchOutreachLog,
} from '../api/backend.js';

const CACHE_TTL_MS       = 5 * 60 * 1000;
const LS_KEY             = 'sheetData_v1';
const LS_MAX_AGE_MS      = 8 * 60 * 60 * 1000; // localStorage stale after 8 hours

let _cache = null;
let _cacheTime = 0;

function lsRead() {
  try {
    const raw = localStorage.getItem(LS_KEY);
    if (!raw) return null;
    const { data, ts } = JSON.parse(raw);
    if (Date.now() - ts > LS_MAX_AGE_MS) return null;
    return { data, ts };
  } catch { return null; }
}

function lsWrite(data) {
  try { localStorage.setItem(LS_KEY, JSON.stringify({ data, ts: Date.now() })); }
  catch {} // storage quota — silently skip
}

export function useSheetData({ getToken, skip = false }) {
  // Seed state from localStorage so the UI renders immediately on mount
  const lsEntry = lsRead();
  const [data, setData]         = useState(lsEntry?.data ?? null);
  const [loading, setLoading]   = useState(!lsEntry); // only show spinner if no cached data
  const [error, setError]       = useState(null);
  const [lastLoaded, setLastLoaded] = useState(lsEntry ? new Date(lsEntry.ts) : null);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  const load = useCallback(async (force = false) => {
    if (skip) return;

    // In-memory cache hit (same session, no force refresh)
    if (!force && _cache && Date.now() - _cacheTime < CACHE_TTL_MS) {
      setData(_cache);
      setLastLoaded(new Date(_cacheTime));
      return;
    }

    // If we have stale localStorage data, show it immediately and fetch silently
    const hasCachedUI = !!_cache || !!lsRead();
    if (!hasCachedUI) setLoading(true);
    setError(null);

    try {
      // Primary batch — single round-trip to /api/today which runs all 6 BQ
      // queries in parallel server-side and returns a smart-filtered contact set.
      const bundle = await fetchTodayBundle(getToken);

      const baseContacts = bundle.contacts ?? [];
      const result = {
        contacts:                 baseContacts,
        firms:                    bundle.firms        ?? [],
        demos:                    bundle.demos        ?? [],
        trials:                   bundle.trials       ?? [],
        customers:                [],
        outreachLog:              bundle.outreachLog  ?? [],
        config:                   bundle.config       ?? {},
        contactEmailMap:          [],
        calendarUnknownAttendees: [],
        gmailUnmatched:           [],
      };

      _cache = result;
      _cacheTime = Date.now();
      lsWrite(result);

      if (mountedRef.current) {
        setData(result);
        setLastLoaded(new Date());
        setLoading(false);
      }

      // Secondary batch — non-blocking, updates UI when ready.
      // These are lower-priority and don't block the initial render.
      Promise.allSettled([
        fetchContactEmailMap(getToken),
        fetchCalendarUnknownAttendees(getToken),
        fetchGmailUnmatched(getToken),
      ]).then(([emailMapSettled, calendarSettled, unmatchedSettled]) => {
        if (!mountedRef.current) return;
        const emailMapRows  = emailMapSettled.status === 'fulfilled' ? (emailMapSettled.value?.rows ?? []) : [];
        const calendarRows  = calendarSettled.status  === 'fulfilled' ? (calendarSettled.value  ?? [])  : [];
        const gmailRows     = unmatchedSettled.status === 'fulfilled' ? (unmatchedSettled.value ?? [])  : [];
        const contactIdSet  = new Set(baseContacts.map((c) => c.contact_id));
        const extraContacts = emailMapRows.filter((c) => c.contact_id && !contactIdSet.has(c.contact_id));
        const next = {
          ...result,
          contacts:                 [...baseContacts, ...extraContacts],
          contactEmailMap:          emailMapRows,
          calendarUnknownAttendees: Array.isArray(calendarRows) ? calendarRows : [],
          gmailUnmatched:           Array.isArray(gmailRows)    ? gmailRows    : [],
        };
        _cache = next;
        _cacheTime = Date.now();
        lsWrite(next);
        setData(next);
      });
    } catch (e) {
      console.error('[useSheetData] load error:', e);
      if (mountedRef.current) {
        setError(e.message);
        setLoading(false);
      }
    }
  }, [getToken, skip]);

  useEffect(() => { load(); }, [load]);

  const refresh = useCallback(() => load(true), [load]);

  // Optimistic local mutation (SWR-style "mutate"): apply a known write result
  // to the cached bundle immediately so the UI updates the moment a save
  // completes, while refresh() brings server truth in the background. Also
  // updates the module cache + localStorage snapshot so a quick tab switch
  // doesn't resurrect the pre-write state.
  const patchBundle = useCallback((updater) => {
    setData((prev) => {
      if (!prev) return prev;
      const next = updater(prev);
      _cache = next;
      _cacheTime = Date.now();
      lsWrite(next);
      return next;
    });
  }, []);

  /** Merge fields into one trial row by trial_id. */
  const patchTrial = useCallback((trialId, fields) => {
    patchBundle((prev) => ({
      ...prev,
      trials: (prev.trials ?? []).map((t) => (t.trial_id === trialId ? { ...t, ...fields } : t)),
    }));
  }, [patchBundle]);

  /** Insert or update a contact in the bundle (so enrichment can resolve it immediately). */
  const upsertContact = useCallback((contact) => {
    if (!contact?.contact_id) return;
    patchBundle((prev) => {
      const contacts = prev.contacts ?? [];
      const exists = contacts.some((c) => c.contact_id === contact.contact_id);
      return {
        ...prev,
        contacts: exists
          ? contacts.map((c) => (c.contact_id === contact.contact_id ? { ...c, ...contact } : c))
          : [contact, ...contacts],
      };
    });
  }, [patchBundle]);

  // Lightweight refresh after Gmail sync — only updates outreachLog + gmailUnmatched.
  const refreshOutreach = useCallback(async () => {
    if (skip || !_cache) return load(true);
    try {
      const [outreachRes, gmailUnmatched] = await Promise.all([
        fetchOutreachLog(getToken, { limit: 0, sinceDays: 60 }),
        fetchGmailUnmatched(getToken),
      ]);
      const result = {
        ..._cache,
        outreachLog:    outreachRes.rows ?? [],
        gmailUnmatched: Array.isArray(gmailUnmatched) ? gmailUnmatched : [],
      };
      _cache = result;
      _cacheTime = Date.now();
      lsWrite(result);
      if (mountedRef.current) {
        setData(result);
        setLastLoaded(new Date());
      }
    } catch (e) {
      console.error('[useSheetData] refreshOutreach error:', e);
    }
  }, [getToken, skip, load]);

  return { data, loading, error, refresh, refreshOutreach, lastLoaded, patchTrial, upsertContact };
}

export function invalidateSheetCache() {
  _cache = null;
  _cacheTime = 0;
  // Don't clear localStorage — stale data is better than a blank spinner on next load.
  // Fresh data will overwrite it once the next fetch completes.
}
